import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import WorkOrderLink from "./Component/WorkOrderEventLog/WorkOrderLink";
import { rememberReturnTo } from "./routes/returnTo";

import Login from "./Pages/Login/Login";
import NotFound from "./Pages/NotFound/NotFound";
import PermissionRoute from "./Component/PermissionRoute/PermissionRoute";
import { ROUTE_ACCESS } from "./permissions/registry";
import PageLoader from "./components/PageLoader";
import Layout from "./Layout/Layout";
import { SidebarProvider } from "./Context/SidebarContext";
import { ContractProvider } from "./Context/ContractContext";
import { getUserSession } from "./function/AuthStorage";
import { startAccessSync } from "./permissions/access";
import "./assets/css/all.css";

import {
  ALL_APPROVE_PERMS,
  ALL_DELETE_PERMS,
  DAILY_EXECUTION_PERMS,
  PROJECT_VIEW_PERMS,
  AI_ASSISTANT_PERMS,
  HOME_ROUTE,
} from "./routes/permissions";

import * as Pages from "./routes/lazyPages";

const {
  DashboardShell, SearchRequests, Orders, WorkOrdersPage, DailyExecutionPage, AccessManagementPage,
  ContractHubPage, CommunicationPage, FinancialRegisterPage, WorkOrderAnalysisPage, ExecutionTrackingPage, MaintenanceCreatePage, EmergencyCreatePage, RehabilitationCreatePage, PrivateProjectCreatePage, DailyWorkPlanPage, TodayWorkPage, ExecutionTeamsPage, ConstructionCreatePage, WorkOrderDraftsPage, EmployeeHubPage,
  MonitoringPage, PdcIndicatorsPage, InternalIndicatorsPage, DataReconciliationPage, WorkOrderImportPage, BulkTransferPage, Project, AddAccount, Accounts, Engineers, AppHomePage,
  SpecialProjects,
  EngStatcis, Consultants, DeleteOrders,
  NotificationPage, Areas, Branches, Offices, Vacations, RehabilitationWorks,
  EngProfile, EngNotification, AcceptDeteleOrders,
  TaskType, AdminProjects, AdminBranchData, ResetPassword, RequestsWaiting,
  OfficeMangerProfile, AllAdminPrivateProject, AiAssistant, AiSettings, Custodies,
  CheckAttendanceAdmin, EngAttendance, MyVacations, CustodiesEngContent,
  ProjectParties, ProjectOwners, AdminProjectCreate, WorkOrderDetailsPage, SupervisionFormPage, MyTasksPage, MySignaturePage, TransactionsPage, DailyActivityPage, UserSignaturesPage, EngPerformancePage, EngineerPerformancePage, KpiSettingsPage,
  PortalLayout, PortalOrdersPage, PortalOrderPage, PortalProfilePage,
} = Pages;

const TX_PERMS = ["Transactions.View", "Transactions.ViewProject", "Transactions.ViewTeam", "Transactions.ViewPersonal"];
const DA_PERMS = ["DailyActivity.View", "DailyActivity.ViewProject", "DailyActivity.ViewTeam", "DailyActivity.ViewPersonal"];
const ENG_PERF_PERMS = ["EngPerformance.View", "EngPerformance.ViewProject", "EngPerformance.ViewTeam", "EngPerformance.ViewPersonal"];

// ─── Route wrapper helpers ────────────────────────────────────────────────
// S  → Suspense محلي: الـ Layout (السايدبار) يفضل ظاهر أثناء تحميل الصفحة
const S = (element) => (
  <Suspense fallback={<PageLoader />}>{element}</Suspense>
);

// P  → صلاحية واحدة مطلوبة
const P = (permission, element, fallback) => (
  <PermissionRoute permission={permission} fallback={fallback}>
    {S(element)}
  </PermissionRoute>
);

// PA → أي صلاحية من القايمة كفاية
const PA = (anyOf, element, fallback) => (
  <PermissionRoute anyOf={anyOf} fallback={fallback}>
    {S(element)}
  </PermissionRoute>
);

// ═════════════════════════════════════════════════════════════════════════
function App() {
  // الـ session بتتقرا مرة واحدة في أول render، عشان الـ refresh ما يعرضش NotFound
  const [userData, setUserData] = useState(() => getUserSession());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // الصلاحيات الفعلية تُحدَّث من الخلفية (دخول، كل دقيقة، عودة للنافذة) — سحب الصلاحية يسري فوراً
  useEffect(() => {
    if (userData?.token) startAccessSync();
  }, [userData]);

  // لو مفيش session ارجع للـ login
  useEffect(() => {
    const user = getUserSession();
    if (!user && location.pathname !== "/" && location.pathname !== "/contractors" && location.pathname !== "/reset-password") {
      // رابط أمر العمل من البريد: يُفتح بعد تسجيل الدخول مباشرة
      rememberReturnTo(location.pathname + location.search);
      navigate("/", { replace: true });
    }
  }, [navigate, location.pathname, location.search]);

  const userType = userData?.userType;
  const isContractorType = String(userType || "").toLowerCase() === "contractor";
  const fb = HOME_ROUTE;
  const toHome = <Navigate to={HOME_ROUTE} replace />;

  /*
    هيكل واحد لكل الحسابات.

    كان نوع الحساب يختار الواجهة: المدير/المشرف بالقائمة الجانبية والهيدر الموحّد، والمهندس/المقاول
    بشريط علوي قديم وصفحة رئيسية قديمة (البانر والصناديق الذهبية)، وأي نوع آخر بلا مسارات أصلاً.
    الآن كل مستخدم مسجّل يدخل نفس Layout (القائمة الجانبية + الهيدر الموحّد + رموز الهوية)،
    والصلاحيات وحدها تحدد ما يظهر في القائمة وما يُفتح من الصفحات والأزرار.
  */
  return (
    <ContractProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Public ── */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Login setUserData={setUserData} />} />
          <Route path="/contractors" element={<Login setUserData={setUserData} portal />} />

          {/* تحديث 69: حساب المقاول يرى بوابته فقط — لا مسارات داخلية ولا Layout عصف */}
          {userData && isContractorType && (
            <Route path="/portal" element={S(<PortalLayout setUserData={setUserData} />)}>
              <Route index element={S(<PortalOrdersPage />)} />
              <Route path="orders/:type/:id" element={S(<PortalOrderPage />)} />
              <Route path="profile" element={S(<PortalProfilePage />)} />
            </Route>
          )}
          {userData && isContractorType && <Route path="*" element={<Navigate to="/portal" replace />} />}

          {userData && !isContractorType && (
            <Route
              element={
                <SidebarProvider>
                  <Layout />
                </SidebarProvider>
              }
            >
              {/* رابط أمر العمل الثابت (البريد والإشعارات) */}
              <Route path="/work-order/:id" element={<WorkOrderLink userType={userType} />} />

              {/* الصفحة الرئيسية الموحّدة: ما تسمح به الصلاحيات فقط */}
              <Route path="/home" element={S(<AppHomePage />)} />

              {/* لوحة المؤشرات العامة: صلاحية «Dashboard.View» */}
              <Route path="/home-page" element={P("Dashboard.View", <DashboardShell />, fb)} />

              {/* بحث الطلبات → أي View من modules المشاريع */}
              <Route path="/search-requests"
                element={PA(PROJECT_VIEW_PERMS, <SearchRequests />, fb)} />

              <Route path="/admin-projects/:type"
                element={PA(PROJECT_VIEW_PERMS, <AdminProjectCreate userData={userData} />, fb)} />

              {/* أوامر العمل → أي View من modules المشاريع أو أنواع الأوامر */}
              {/* تحديث 51: عرض أوامر العمل الموحد (?type= ?contractId= ?basket= ?stage= ?q=) */}
              <Route path="/orders"
                element={PA([...PROJECT_VIEW_PERMS, "WorkOrderType.View"], <WorkOrdersPage />, fb)} />
              {/* العرض السابق لكل الأنواع (يشمل أعمال التأهيل والمشاريع الخاصة وتصدير PDF) — محفوظ دون تغيير */}
              <Route path="/orders/all-types"
                element={PA([...PROJECT_VIEW_PERMS, "WorkOrderType.View"], <Orders />, fb)} />

              {/* تحديث التنفيذ اليومي */}
              <Route path="/daily-execution"
                element={PA(DAILY_EXECUTION_PERMS, <DailyExecutionPage />, fb)} />

              {/* لوحة المتابعة والمؤشّرات */}
              <Route path="/monitoring"
                element={P("ContractWorkflow.View", <MonitoringPage />, fb)} />

              {/* مؤشرات PDC — لوحة مؤشرات أداء أوامر العمل (قراءة فقط) */}
              <Route path="/pdc-indicators"
                element={P("PDCIndicators.View", <PdcIndicatorsPage />, fb)} />
              <Route path="/internal-indicators"
                element={P("InternalIndicators.View", <InternalIndicatorsPage />, fb)} />
              {/* المرحلة 1 — مطابقة الأرقام بين الصفحات (مدير النظام فقط، قراءة فقط) */}
              <Route path="/data-reconciliation"
                element={<PermissionRoute adminOnly>{S(<DataReconciliationPage />)}</PermissionRoute>} />
              {/* تحديث 63 — مركز مؤشرات أداء المهندسين والأنشطة التشغيلية */}
              <Route path="/transactions" element={PA(TX_PERMS, <TransactionsPage />, fb)} />
              <Route path="/reports/daily-activity" element={PA(DA_PERMS, <DailyActivityPage />, fb)} />
              <Route path="/eng-performance" element={PA(ENG_PERF_PERMS, <EngPerformancePage />, fb)} />
              <Route path="/eng-performance/engineer/:id" element={PA(ENG_PERF_PERMS, <EngineerPerformancePage />, fb)} />
              <Route path="/eng-performance/settings" element={P("EngPerformance.ManageSettings", <KpiSettingsPage />, fb)} />
              <Route path="/user-signatures" element={P("SupervisionForms.ManageSignatures", <UserSignaturesPage />, fb)} />

              {/* إنشاء أوامر العمل — صفحة مستقلة لكل نوع */}
              <Route path="/work-orders/new/construction"
                element={P("Construction.Create", <ConstructionCreatePage />, fb)} />
              <Route path="/work-orders/new/maintenance"
                element={P("Maintenance.Create", <MaintenanceCreatePage />, fb)} />
              <Route path="/work-orders/new/emergency"
                element={P("Emergency.Create", <EmergencyCreatePage />, fb)} />
              <Route path="/work-orders/new/rehabilitation"
                element={P("NewProject.Create", <RehabilitationCreatePage />, fb)} />
              <Route path="/work-orders/new/private"
                element={P("PrivateProject.Create", <PrivateProjectCreatePage />, fb)} />

              {/* مسودات أوامر العمل — استكمال ما حُفظ */}
              <Route path="/work-orders/drafts"
                element={P("Construction.Create", <WorkOrderDraftsPage />, fb)} />

              {/* الاستيراد الذكي لأوامر العمل من Excel */}
              {/* تحديث 60: مركز تحليل ومزامنة أوامر العمل عبر Excel */}
              <Route path="/work-orders/analysis"
                element={PA(["WorkOrders.ExcelImport.View", "WorkOrders.ExcelAnalysis.View"], <WorkOrderAnalysisPage />, fb)} />
              {/* تحديث 60: سجل المستخلصات والصرف */}
              <Route path="/financial-register"
                element={PA(["WorkBudget.View", "WorkBudget.Prepare", "WorkBudget.Approve"], <FinancialRegisterPage />, fb)} />
              {/* تحديث 71: النقل الجماعي لأوامر العمل بين السلال بواسطة Excel */}
              <Route path="/work-orders/bulk-transfer"
                element={P("BulkWorkOrderBasketTransfer.Transfer", <BulkTransferPage />, fb)} />
              <Route path="/work-orders/import"
                element={P("WorkOrders.ExcelImport.View", <WorkOrderImportPage />, fb)} />

              {/* متابعة التنفيذ */}
              <Route path="/execution-tracking"
                element={P("ContractWorkflow.View", <ExecutionTrackingPage />, fb)} />

              {/* جدول الأعمال اليومية وأعمال تُنفذ اليوم وفرق التنفيذ */}
              <Route path="/daily-work-plan"
                element={P("DailyWork.View", <DailyWorkPlanPage />, fb)} />
              <Route path="/today-work"
                element={PA(["DailyWork.ViewToday", "DailyWork.View"], <TodayWorkPage />, fb)} />
              <Route path="/execution-teams"
                element={PA(["ExecutionTeams.View", "ExecutionTeams.Manage"], <ExecutionTeamsPage />, fb)} />

              {/* إعدادات العقد — مركز واحد */}
              <Route path="/contract-settings"
                element={P("ContractWorkflow.View", <ContractHubPage />, fb)} />
              <Route path="/contract-workflow" element={<Navigate to="/contract-settings" replace />} />
              <Route path="/settings" element={<Navigate to="/contract-settings" replace />} />

              {/* إدارة الصلاحيات */}
              <Route path="/access-management"
                element={P("Users.ManagePermissions", <AccessManagementPage />, fb)} />
              <Route path="/add-permission" element={<Navigate to="/access-management" replace />} />
              <Route path="/settings/permissions" element={<Navigate to="/access-management" replace />} />

              {/* تفاصيل أمر العمل/المشروع → أي View */}
              <Route path="/project/:type/:id"
                element={PA(PROJECT_VIEW_PERMS, <Project userData={userData} />, fb)} />

              {/* تحديث 53: تعبئة نموذج الإشراف (الصلاحية الفعلية يفرضها الخادم) */}
              <Route path="/supervision/:type/:id/:instId"
                element={PA(PROJECT_VIEW_PERMS, <SupervisionFormPage />, fb)} />
              {/* روابط أنواع أوامر العمل القديمة: الإضافة → صفحة الإنشاء الجديدة، والتفاصيل بصفحة التفاصيل الموحّدة */}
              <Route path="/construction-projects"
                element={P("Construction.Create", <Navigate to="/work-orders/new/construction" replace />, fb)} />
              <Route path="/construction-projects/:id"
                element={P("Construction.View", <WorkOrderDetailsPage typeKey="construction" />, fb)} />
              <Route path="/maintain-projects"
                element={P("Maintenance.Create", <Navigate to="/work-orders/new/maintenance" replace />, fb)} />
              <Route path="/maintain-projects/:id"
                element={P("Maintenance.View", <WorkOrderDetailsPage typeKey="maintenance" />, fb)} />
              <Route path="/emergency-projects"
                element={P("Emergency.Create", <Navigate to="/work-orders/new/emergency" replace />, fb)} />
              <Route path="/emergency-projects/:id"
                element={P("Emergency.View", <WorkOrderDetailsPage typeKey="emergency" />, fb)} />
              <Route path="/rehabilitation-works"
                element={P("NewProject.Create", <Navigate to="/work-orders/new/rehabilitation" replace />, fb)} />
              <Route path="/rehabilitation-works/:id"
                element={P("NewProject.View", <RehabilitationWorks userData={userData} />, fb)} />
              <Route path="/special-projects"
                element={P("PrivateProject.Create", <Navigate to="/work-orders/new/private" replace />, fb)} />
              <Route path="/special-projects/:id"
                element={P("PrivateProject.View", <SpecialProjects userData={userData} />, fb)} />

              {/* المهندسون → Employees.View */}
              <Route path="/engineers"
                element={P("Employees.View", <Engineers />, fb)} />

              {/* الاستشاريون → Consultant.View */}
              <Route path="/consultants"
                element={P("Consultant.View", <Consultants />, fb)} />

              {/* كل المشاريع → أي View */}
              <Route path="/admin-projects"
                element={PA(PROJECT_VIEW_PERMS, <AdminProjects />, fb)} />

              {/* المشاريع الخاصة → PrivateProject.View */}
              <Route path="/all-admin-private-project"
                element={P("PrivateProject.View", <AllAdminPrivateProject />, fb)} />

              {/* المشاريع المحذوفة / الاعتماد / الحذف */}
              <Route path="/deleted-projects"
                element={PA(ALL_DELETE_PERMS, <DeleteOrders />, fb)} />
              <Route path="/accept-requests"
                element={PA(ALL_APPROVE_PERMS, <RequestsWaiting />, fb)} />
              <Route path="/accept-delete"
                element={PA(ALL_DELETE_PERMS, <AcceptDeteleOrders />, fb)} />

              {/* بيانات الفرع والمكتب وأنواع المهام */}
              <Route path="/admin-branch-data"
                element={P("Branch.View", <AdminBranchData />, fb)} />
              <Route path="/manager-profile"
                element={P("Office.View", <OfficeMangerProfile />, fb)} />
              <Route path="/tasktype"
                element={P("WorkOrderType.View", <TaskType />, fb)} />

              {/* الحسابات */}
              <Route path="/add-account"
                element={P("Users.Create", <AddAccount />, fb)} />
              <Route path="/add-account/:id"
                element={P("Users.Create", <AddAccount />, fb)} />
              <Route path="/accounts"
                element={P("Users.View", <Accounts />, fb)} />

              {/* الإشعارات */}
              <Route path="/notification"
                element={P("Notifications.View", <NotificationPage />, fb)} />
              <Route path="/eng-notification" element={S(<EngNotification />)} />

              {/* البيانات الأساسية */}
              <Route path="/district"
                element={P("Neighborhood.View", <Areas />, fb)} />
              <Route path="/branches"
                element={P("Branch.View", <Branches />, fb)} />
              <Route path="/offices"
                element={P("Office.View", <Offices />, fb)} />
              <Route path="/project-parties"
                element={P("ProjectParty.View", <ProjectParties />, fb)} />
              <Route path="/project-owners"
                element={P("ProjectOwner.View", <ProjectOwners />, fb)} />

              {/* الموارد البشرية */}
              <Route path="/employees"
                element={P("Employees.View", <EmployeeHubPage />, fb)} />
              <Route path="/vacations"
                element={P("LeaveRequest.View", <Vacations />, fb)} />
              <Route path="/check-attendance"
                element={P("Attendance.View", <CheckAttendanceAdmin />, fb)} />
              <Route path="/custodies"
                element={P("Custody.View", <Custodies />, fb)} />

              {/* خدمات المستخدم نفسه (كانت في واجهة المهندس القديمة) */}
              <Route path="/eng-attendance"
                element={PA(["Attendance.View", "Attendance.CheckIn"], <EngAttendance />, fb)} />
              <Route path="/myvacations"
                element={PA(["LeaveRequest.View", "LeaveRequest.Create"], <MyVacations />, fb)} />
              <Route path="/custodieseng"
                element={P("Custody.View", <CustodiesEngContent />, fb)} />

              {/* الملف الشخصي: بيانات المستخدم نفسه — متاح لكل مستخدم مسجّل */}
              <Route path="/profile" element={S(<EngProfile />)} />
              {/* تحديث 62 — «مهامي»: لكل مستخدم (يرى مهامه هو فقط) */}
              <Route path="/my-tasks" element={S(<MyTasksPage />)} />
              <Route path="/my-signature" element={S(<MySignaturePage />)} />

              {/* تحديث 59: مركز التواصل والتعاميم — لكل مستخدم مسجّل؛ الخادم يعزل المشروعات ويطبق الصلاحيات */}
              {/* المرحلة 0: حماية مسار التواصل بنفس قاعدة القائمة (ROUTE_ACCESS) حتى لا يُفتح بالرابط لمن لا تظهر له */}
              <Route path="/communication" element={PA(ROUTE_ACCESS["/communication"].anyOf, <CommunicationPage />, fb)} />
              <Route path="/eng-profile" element={<Navigate to="/profile" replace />} />

              {/* صفحات عامة قديمة — داخل الهيكل الموحّد */}
              <Route path="/statics" element={S(<EngStatcis />)} />
              {/* المساعد الذكي (AI Operations Copilot) — بصلاحيات المستخدم؛ «بيانات التشغيل» القديمة تحولت إليه */}
              <Route path="/ai-assistant" element={PA(AI_ASSISTANT_PERMS, <AiAssistant />, fb)} />
              <Route path="/boot-info" element={<Navigate to="/ai-assistant" replace />} />
              {/* إعدادات النظام ← الذكاء الاصطناعي (تحديث 46) */}
              <Route path="/ai-settings" element={PA(["AiAssistant.ManageSettings", "AiAssistant.ViewUsage"], <AiSettings />, fb)} />
              {/* قوائم الطلبات القديمة (صفحات بانر) → بحث الطلبات الموحّد */}
              <Route path="/requests/:namepage/:name" element={<Navigate to="/search-requests" replace />} />

              {/* الصفحة الرئيسية القديمة وصفحاتها الفرعية حلّت محلها الرئيسية الموحّدة */}
              <Route path="/main-page" element={toHome} />
              <Route path="/sub-page/:name" element={toHome} />
              <Route path="/electricRequests" element={toHome} />
              <Route path="/projects" element={toHome} />
              <Route path="/submit-application" element={toHome} />
              <Route path="/submit-application/:id" element={toHome} />
              <Route path="/contactus" element={toHome} />
              <Route path="/about" element={toHome} />

              <Route path="*" element={<NotFound />} />
            </Route>
          )}

          {!userData && <Route path="*" element={<NotFound />} />}
        </Routes>
      </Suspense>
    </ContractProvider>
  );
}

export default App;